import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatchApp } from "../../lib/redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearLoaderWithTime from "../../components/Loaders/LinearLoaderWithTime";
import { AdleLogoLoader } from "../../assets/svg/AdleLogoLoader";
import {
  OWNER_LOGIN_AS_CONNECTING,
  OWNER_LOGIN_AS_ERROR_GENERIC,
  OWNER_LOGIN_AS_ERROR_TITLE,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { setSkipLastLogin, startLogout } from "../../actions/auth";
import { startExchangeOwnerLoginAsClientLink } from "../../actions/owner";
import { setPrivilegedLoginInStorage } from "../../utils/privilegedLoginStorage";
import { getOwnerLoginAsExchangeErrorMessageKey } from "../../utils/ownerLoginAsErrors";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { IS_ADLE_DOMAIN } from "../../utils/constants";

import { LaunchError } from "../../utils/errors";

const OWNER_LOGIN_AS_LOADER_TIME_MS = 25 * 1000;

/**
 * Survives AppRouter remounts caused by startLogout → GLOBAL_RESET_STATE
 * (isLoadingAuth flips true and unmounts <Routes>). A useRef cannot.
 */
const consumedOwnerLoginAsHashes = new Set();
let ownerLoginAsFlowStarted = false;

const OwnerLoginAsContent = ({ children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 2,
    }}
  >
    <Box
      sx={{
        width: "100%",
        maxWidth: 700,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      {children}
    </Box>
  </Box>
);

const OwnerLoginAs = () => {
  const [status, setStatus] = useState("loading");
  const [errorMessageKey, setErrorMessageKey] = useState(
    OWNER_LOGIN_AS_ERROR_GENERIC
  );

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const hash = params.get("hash");

  const stripHashFromUrl = () => {
    navigate("/owner-login-as", { replace: true });
  };

  const onHandleOwnerLoginAs = async (loginHash) => {
    if (!loginHash || consumedOwnerLoginAsHashes.has(loginHash)) {
      // Sibling instance already owns this one-time hash (post-remount).
      // If sign-in already landed, recover to home instead of spinning forever.
      if (auth.currentUser) {
        navigate("/", { replace: true });
      }
      return;
    }

    consumedOwnerLoginAsHashes.add(loginHash);
    ownerLoginAsFlowStarted = true;

    try {
      if (auth.currentUser) {
        await dispatch(startLogout());
      }

      const response = await dispatch(
        startExchangeOwnerLoginAsClientLink({ hash: loginHash })
      );

      if (!response.ok) {
        stripHashFromUrl();
        setErrorMessageKey(
          getOwnerLoginAsExchangeErrorMessageKey(response.code)
        );
        setStatus("error");
        return;
      }

      const customToken = response?.data?.customToken;
      if (!customToken) {
        stripHashFromUrl();
        setErrorMessageKey(OWNER_LOGIN_AS_ERROR_GENERIC);
        setStatus("error");
        return;
      }

      dispatch(setSkipLastLogin(true));
      const responseLogin = await signInWithCustomToken(auth, customToken);
      if (responseLogin?.user?.uid) {
        setPrivilegedLoginInStorage({ uid: responseLogin.user.uid });
      }

      // replace:/ clears the hash entry from history — no pre-strip navigate,
      // which could race a second instance and yank the user back here.
      navigate("/", { replace: true });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onHandleOwnerLoginAs",
          capture: "exception",
        })
      );
      console.error("[OwnerLoginAs] Unexpected error");
      stripHashFromUrl();
      setErrorMessageKey(OWNER_LOGIN_AS_ERROR_GENERIC);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (!hash) {
      // Mid-flow remount after strip, or empty link on first paint.
      if (ownerLoginAsFlowStarted) {
        if (auth.currentUser) {
          navigate("/", { replace: true });
        }
        return;
      }

      setErrorMessageKey(
        getOwnerLoginAsExchangeErrorMessageKey("missingParameters")
      );
      setStatus("error");
      return;
    }

    onHandleOwnerLoginAs(hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  if (status === "error") {
    return (
      <OwnerLoginAsContent>
        {IS_ADLE_DOMAIN && <AdleLogoLoader />}
        <Typography variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
          {t(OWNER_LOGIN_AS_ERROR_TITLE)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t(errorMessageKey)}
        </Typography>
      </OwnerLoginAsContent>
    );
  }

  return (
    <OwnerLoginAsContent>
      {IS_ADLE_DOMAIN && <AdleLogoLoader />}
      <LinearLoaderWithTime
        labelSide="bottom"
        label={t(OWNER_LOGIN_AS_CONNECTING)}
        time={OWNER_LOGIN_AS_LOADER_TIME_MS}
        sx={{
          mt: IS_ADLE_DOMAIN ? 3 : 0,
          width: "100%",
        }}
      />
    </OwnerLoginAsContent>
  );
};

export default OwnerLoginAs;
